import type { Conversation, WorkspaceMode } from '@auw/workspace-utils';
import { applyConversationMemoryPolicy } from './conversationMemory';
import { normalizeWorkspaceOrder } from './conversationTree';

export interface ConversationEntityStateSnapshot {
  conversations: Record<string, Conversation>;
  conversationOrder: string[];
  workspaceOrder: string[];
  activeId: string | null;
  totalItemCount: number;
  totalItemCountByMode: Partial<Record<WorkspaceMode, number>>;
  workspaceHydrated: boolean;
}

interface MergeConversationBatchOptions {
  order?: string[];
  workspaceOrder?: string[];
  totalCount?: number;
  modeCount?: {
    mode: WorkspaceMode;
    count: number;
  };
  workspaceHydrated?: boolean;
}

interface UpsertConversationOptions {
  prepend?: boolean;
}

export function mergeConversationEntity(
  existing: Conversation | undefined,
  incoming: Conversation,
): Conversation {
  if (!existing) return incoming;

  return {
    ...incoming,
    messages: existing.messages,
    historyLoaded: existing.historyLoaded,
  };
}

export function mergeConversationBatch(
  state: ConversationEntityStateSnapshot,
  incoming: Record<string, Conversation>,
  options?: MergeConversationBatchOptions,
): Pick<
  ConversationEntityStateSnapshot,
  'conversations' | 'conversationOrder' | 'workspaceOrder' | 'totalItemCount' | 'totalItemCountByMode' | 'workspaceHydrated'
> {
  const conversations: Record<string, Conversation> = { ...state.conversations };
  for (const [id, conversation] of Object.entries(incoming)) {
    conversations[id] = mergeConversationEntity(conversations[id], conversation);
  }

  const nextConversationOrder = options?.order?.length
    ? normalizeWorkspaceOrder(options.order, state.conversationOrder, conversations)
    : state.conversationOrder;
  const nextWorkspaceOrder = options?.workspaceOrder?.length
    ? normalizeWorkspaceOrder(options.workspaceOrder, state.workspaceOrder, conversations)
    : state.workspaceOrder;

  const memoryManaged = applyConversationMemoryPolicy(conversations, state.activeId);
  return {
    conversations: memoryManaged,
    conversationOrder: nextConversationOrder,
    workspaceOrder: nextWorkspaceOrder,
    totalItemCount: options?.totalCount ?? state.totalItemCount,
    totalItemCountByMode: options?.modeCount
      ? { ...state.totalItemCountByMode, [options.modeCount.mode]: options.modeCount.count }
      : state.totalItemCountByMode,
    workspaceHydrated: options?.workspaceHydrated ?? state.workspaceHydrated,
  };
}

export function upsertConversationEntity(
  state: ConversationEntityStateSnapshot,
  incoming: Conversation,
  options?: UpsertConversationOptions,
): Pick<ConversationEntityStateSnapshot, 'conversations' | 'conversationOrder' | 'workspaceOrder'> {
  const prepend = options?.prepend ?? false;
  const nextOrder = prepend
    ? [incoming.id, ...state.conversationOrder]
    : state.conversationOrder.includes(incoming.id)
      ? state.conversationOrder
      : [...state.conversationOrder, incoming.id];
  const nextWorkspaceOrder = prepend
    ? [incoming.id, ...state.workspaceOrder]
    : state.workspaceOrder.includes(incoming.id)
      ? state.workspaceOrder
      : [...state.workspaceOrder, incoming.id];

  const merged = mergeConversationBatch(
    state,
    { [incoming.id]: incoming },
    {
      order: nextOrder,
      workspaceOrder: nextWorkspaceOrder,
    },
  );

  return {
    conversations: merged.conversations,
    conversationOrder: merged.conversationOrder,
    workspaceOrder: merged.workspaceOrder,
  };
}
